import { useEffect, useRef, useState } from 'react';
import Cookies from 'js-cookie';

import { Box, BoxProps, Divider, Flex, Heading, List, ListIcon, ListItem, Skeleton, Text } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { Card } from '~/components/card';
import { Link } from '~/components/link';
import { trackEvent } from '~/features/analytics/track-event';
import { useUserPaymentMethods } from '~/features/checkout/hooks/use-user-payment-methods';
import { billingFrequencySchema } from '~/features/checkout/models/checkout';
import {
  SUBSCRIPTION_REFERRER_PROGRAM_KEY_COOKIE,
  SUBSCRIPTION_REFERRER_PROGRAM_TITLE,
  SUBSCRIPTION_REFERRER_URL_COOKIE,
} from '~/features/cookies/constants';
import { MobileStickyBanner } from '~/features/experiments/ab_plp_redesign_2025/components/mobile-sticky-banner';
import { CheckIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { useCurrency } from '~/features/internationalization/hooks/use-currency';
import { usePricePlans } from '~/features/plans/hooks/use-price-plans';

import { FREE_TRIAL_LENGTH_IN_DAYS, getFreeTrialChargeDate } from '../utils/get-free-trial-charge-date';

const TRIAL_INCLUDES = [
  'Full access to all 50+ lessons in Agentic AI',
  'Build and submit 4 portfolio-ready projects',
  'Personalized feedback from industry mentors',
];

const CTA_EVENT_LABEL = 'agentic-ai-ft-2026 cta clicked';

export type FreeTrialPriceCardProps = {
  isMobile?: boolean;
  programKey: string;
  programTitle: string;
} & BoxProps;

export function FreeTrialPriceCard({
  isMobile = false,
  programKey,
  programTitle,
  ...cardProps
}: FreeTrialPriceCardProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [chargeDate, setChargeDate] = useState<string | null>(null);
  const [showMobileBanner, setShowMobileBanner] = useState(false);
  const { data: paymentMethods } = useUserPaymentMethods();
  const ctaHref = `/checkout/individual/monthly/${paymentMethods?.length ? 'saved-card' : 'credit-card'}?trialingKey=${programKey}`;
  const { currency, resolved: currencyResolved } = useCurrency();
  const { data: plans } = usePricePlans(currency);
  const monthlyPlan = plans?.prices[billingFrequencySchema.enum.monthly]?.individual;
  const monthlyPrice = monthlyPlan?.displayPricePerMonth;
  const monthlyStrikethrough = monthlyPlan?.strikeThroughDisplayPricePerMonth;
  const monthlyCouponPercentOff = monthlyPlan?.couponApplied ? monthlyPlan.couponPercentOff : null;
  const formattedZeroPrice = currencyResolved
    ? new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(0)
    : null;

  useEffect(() => {
    setChargeDate(getFreeTrialChargeDate());
  }, []);

  useEffect(() => {
    const node = containerRef.current;
    if (!isMobile || !node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          setShowMobileBanner(!entry.isIntersecting && entry.boundingClientRect.top < 0);
        }
      },
      { threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isMobile]);

  function handleCtaClick() {
    Cookies.set(SUBSCRIPTION_REFERRER_URL_COOKIE, window.location.pathname, { expires: 1 });
    Cookies.set(SUBSCRIPTION_REFERRER_PROGRAM_KEY_COOKIE, programKey, { expires: 1 });
    Cookies.set(SUBSCRIPTION_REFERRER_PROGRAM_TITLE, programTitle, { expires: 1 });

    trackEvent({
      name: 'Product Page CTA Clicked',
      type: 'click',
      label: CTA_EVENT_LABEL,
      additionalAttributes: {
        product_ids: [programKey],
      },
    });
  }

  const ctaButton = (
    <ButtonLink
      buttonProps={{
        'aria-label': 'Start your free trial of the Agentic AI Nanodegree',
        colorScheme: 'blue',
        onClick: handleCtaClick,
        width: '100%',
      }}
      linkProps={{
        href: ctaHref,
      }}
    >
      Start for free
    </ButtonLink>
  );

  return (
    <>
      <Card
        ref={containerRef}
        bgColor='white'
        borderColor='supportBlue.20'
        borderRadius='12px'
        data-testid='agentic-ai-ft-2026-price-card'
        display='flex'
        flexDirection='column'
        overflow='hidden'
        padding={0}
        {...cardProps}
      >
        <Flex flexDirection='column' padding='16px 20px'>
          {/* Headline + subheadline */}
          <Flex flexDirection='column' gap={1} paddingBottom={2}>
            <Heading as='h2' fontSize='lg' fontWeight='700'>
              Learn Agentic AI for free
            </Heading>
            <Text color='supportGray.70' fontSize='sm'>
              Watch lessons, try projects, get mentor feedback. Cancel anytime.
            </Text>
          </Flex>

          <Divider borderColor='gray.200' />

          {/* Pricing */}
          <Flex flexDirection='column' gap={1} paddingY={3}>
            <Flex alignItems='baseline' gap={2} justifyContent='center'>
              {formattedZeroPrice ? (
                <Heading as='p' fontSize='5xl' fontWeight='700' lineHeight='1'>
                  {formattedZeroPrice}
                </Heading>
              ) : (
                <Skeleton height='3rem' width='60px' />
              )}
              <Text color='supportGray.70' fontSize='md'>
                today
              </Text>
            </Flex>
            <Flex
              alignItems='center'
              color='supportGray.70'
              flexWrap='wrap'
              gap={1}
              justifyContent='center'
              minHeight='1.25rem'
            >
              {chargeDate && (
                <>
                  <Text fontSize='sm'>Then</Text>
                  {monthlyStrikethrough && (
                    <Text as='del' fontSize='sm' textDecoration='line-through'>
                      {monthlyStrikethrough}
                    </Text>
                  )}
                  {monthlyPrice ? (
                    <Text as='strong' fontSize='sm' fontWeight='inherit'>
                      {monthlyPrice}/mo
                    </Text>
                  ) : (
                    <Skeleton height='1em' verticalAlign='middle' width='48px' />
                  )}
                  {typeof monthlyCouponPercentOff === 'number' && (
                    <Text color='green.500' fontSize='sm' fontWeight='600'>
                      ({monthlyCouponPercentOff}% off)
                    </Text>
                  )}
                  <Text fontSize='sm'>starting</Text>
                  <Text as='strong' fontSize='sm' fontWeight='inherit'>
                    {chargeDate}
                  </Text>
                </>
              )}
            </Flex>
          </Flex>

          <Divider borderColor='gray.200' />

          {/* Free trial includes — tinted green section */}
          <Flex backgroundColor='green.50' borderRadius='md' flexDirection='column' gap={2} marginY={3} padding={3}>
            <Heading as='h3' color='green.600' fontSize='sm' fontWeight='700' textTransform='none'>
              Your {FREE_TRIAL_LENGTH_IN_DAYS}-day free trial includes
            </Heading>
            <List display='flex' flexDirection='column' gap={2}>
              {TRIAL_INCLUDES.map((item) => (
                <ListItem key={item} display='flex' fontSize='sm'>
                  <ListIcon alignSelf='flex-start' as={CheckIcon} color='green.500' height='20px' width='20px' />
                  <Text as='span' fontSize='sm'>
                    {item}
                  </Text>
                </ListItem>
              ))}
            </List>
          </Flex>

          <Divider borderColor='gray.200' />

          {/* After-trial paragraph */}
          <Text color='supportGray.70' fontSize='sm' paddingY={3}>
            After free trial, your{' '}
            <Box as='strong' fontWeight='700'>
              Udacity subscription
            </Box>{' '}
            starts — unlocking 500+ programs in AI, Data & more, plus career services and a community of 10,000+
            professionals.
          </Text>

          <Divider borderColor='gray.200' />

          {/* CTA + disclaimer + legal */}
          <Flex flexDirection='column' gap={2} paddingTop={3}>
            {ctaButton}
            <Text color='supportGray.60' fontSize='xs' minHeight='1rem' textAlign='center'>
              {chargeDate ? `Cancel anytime before ${chargeDate}, you won't be charged.` : '\u00A0'}
            </Text>
            <Text color='supportGray.60' fontSize='2xs' textAlign='center'>
              {'By continuing, you agree to '}
              <Link
                color='supportGray.60'
                fontSize='2xs'
                href='/legal/terms-of-use'
                textDecoration='underline'
                isExternal
              >
                Terms
              </Link>
              {' and '}
              <Link color='supportGray.60' fontSize='2xs' href='/legal/privacy' textDecoration='underline' isExternal>
                Privacy Policy
              </Link>
              .
            </Text>
          </Flex>

          <Divider borderColor='gray.200' marginTop={3} />

          {/* View plans */}
          <Box paddingTop={4} textAlign='center'>
            <Link
              color='supportBlue.100'
              fontSize='sm'
              fontWeight='500'
              href='/plans'
              onClick={() =>
                trackEvent({
                  name: 'Product Page CTA Clicked',
                  type: 'click',
                  label: 'agentic-ai-ft-2026 view plans clicked',
                  additionalAttributes: {
                    product_ids: [programKey],
                  },
                })
              }
            >
              Ready to subscribe now? View plans
            </Link>
          </Box>
        </Flex>
      </Card>
      {isMobile && <MobileStickyBanner buttonSlot={ctaButton} isVisible={showMobileBanner} />}
    </>
  );
}
